import {type MetaFunction} from '@remix-run/node'
import {Link} from '@remix-run/react'
import {getDayOfYear} from 'date-fns'

import {Page} from '#app/components/layout'
import {Button} from '#app/ui/button'
import {Book, BookPlus, Lightbulb, LogIn, ReceiptText} from '#app/ui/icons'
import {BibliophileImage} from '#app/ui/images/bibliophile'
import {BookLoverImage} from '#app/ui/images/book-lover'
import {BookReadingImage} from '#app/ui/images/book-reading'
import {BookshelvesImage} from '#app/ui/images/bookshelves'
import {EducationImage} from '#app/ui/images/education'
import {ReadingTimeImage} from '#app/ui/images/reading-time'
import {H1, Lead} from '#app/ui/typography'
import {ProviderConnectionForm} from '#app/utils/connections'
import {useOptionalUser} from '#app/utils/user'

export const meta: MetaFunction = () => {
  return [{title: ENV.APP_NAME}, {name: 'description', content: 'Welcome in our library!'}]
}

export default function Index() {
  return (
    <Page.Container className="grid lg:grid-cols-2 lg:gap-8">
      <div className="max-w-prose">
        <WelcomeText />
        <div className="mt-8 flex flex-wrap gap-4">
          <Actions />
        </div>
      </div>
      <div className="flex max-h-[60vh] grow">
        <RandomImage />
      </div>
    </Page.Container>
  )
}

function WelcomeText() {
  const user = useOptionalUser()

  if (user) {
    return (
      <>
        <H1>Hello, {user.firstName}!</H1>
        <Lead>
          Welcome to our library! You can explore our books, check existing proposals or suggest a
          book that you would like to read.
        </Lead>
      </>
    )
  }

  return (
    <>
      <H1>
        Welcome to <br />
        {ENV.APP_NAME}!
      </H1>
      <Lead>Please login to explore our books and make suggestions for new ones.</Lead>
    </>
  )
}

function Actions() {
  const user = useOptionalUser()

  if (user) {
    return (
      <>
        <Button asChild>
          <Link to="proposals/new">
            <BookPlus />
            Suggest a Book
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="books">
            <Book />
            Books
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="proposals">
            <Lightbulb />
            Proposals
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="rules">
            <ReceiptText />
            Rules
          </Link>
        </Button>
      </>
    )
  }

  return (
    <>
      <ProviderConnectionForm
        providerName="microsoft"
        buttonProps={{
          children: (
            <div className="flex gap-3">
              <LogIn />
              Login
            </div>
          ),
        }}
      />
      <Button asChild variant="outline">
        <Link to="rules">
          <ReceiptText />
          Rules
        </Link>
      </Button>
    </>
  )
}

/**
 * Get different image every day of week
 */
function RandomImage() {
  const imageComponents = [
    BibliophileImage,
    BookLoverImage,
    BookshelvesImage,
    BookReadingImage,
    EducationImage,
    ReadingTimeImage,
  ]

  const index = getDayOfYear(new Date()) % imageComponents.length
  const Image = imageComponents[index]!

  return <Image />
}
